/**
 * GNSS Job History Service
 * 
 * Higher-level interface for managing GNSS job history
 * Handles job lifecycle, result caching, and retrieval
 */

import {
  GNSSProcessingConfig,
  GNSSProcessingResult,
  GNSSJobStatus,
} from '../types';
import {
  getGNSSJobDatabase,
  StoredGNSSJob,
  GNSSJobQuery,
} from './gnssJobDatabase';

export interface JobHistoryEntry {
  jobId: string;
  filename: string;
  config: GNSSProcessingConfig;
  status: GNSSJobStatus['status'];
  uploadedAt: Date;
  startedAt?: Date;
  completedAt?: Date;
  processingDuration?: number;
  result?: GNSSProcessingResult;
  error?: string;
  fileSize: number;
}

export interface JobSummary {
  jobId: string;
  filename: string;
  status: string;
  uploadedAt: string;
  completedAt?: string;
  processingTime?: string;
  resultsSummary?: {
    pointsProcessed: number;
    pdop: number;
    horizontalRMS: number;
    verticalRMS: number;
  };
}

class GNSSJobHistoryService {
  /**
   * Save a new job to history
   */
  async saveJob(
    jobId: string,
    filename: string,
    config: GNSSProcessingConfig,
    fileSize: number
  ): Promise<void> {
    const db = await getGNSSJobDatabase();

    const job: StoredGNSSJob = {
      jobId,
      filename,
      config,
      fileSize,
      status: 'pending',
      progress: 0,
      currentStep: 'Initializing...',
      uploadedAt: new Date().toISOString(),
    };

    await db.storeJob(job);
  }

  /**
   * Update job progress
   */
  async updateProgress(
    jobId: string,
    progress: number,
    currentStep: string
  ): Promise<void> {
    const db = await getGNSSJobDatabase();
    await db.updateJobStatus(jobId, 'processing', {
      progress,
      currentStep,
      startedAt: new Date().toISOString(),
    });
  }

  /**
   * Mark job as completed
   */
  async markCompleted(
    jobId: string,
    result: GNSSProcessingResult,
    processingDuration: number
  ): Promise<void> {
    const db = await getGNSSJobDatabase();
    await db.updateJobStatus(jobId, 'completed', {
      result,
      completedAt: new Date().toISOString(),
      processingDuration,
      progress: 100,
      currentStep: 'Complete',
    });
  }

  /**
   * Mark job as failed
   */
  async markFailed(jobId: string, error: string): Promise<void> {
    const db = await getGNSSJobDatabase();
    await db.updateJobStatus(jobId, 'failed', {
      error,
      completedAt: new Date().toISOString(),
      currentStep: 'Failed',
    });
  }

  /**
   * Get job details
   */
  async getJob(jobId: string): Promise<JobHistoryEntry | null> {
    const db = await getGNSSJobDatabase();
    const stored = await db.getJob(jobId);

    if (!stored) return null;

    return this.convertToHistoryEntry(stored);
  }

  /**
   * Get recent jobs
   */
  async getRecentJobs(limit = 20): Promise<JobSummary[]> {
    const db = await getGNSSJobDatabase();
    const jobs = await db.queryJobs({
      limit,
      sortBy: 'uploadedAt',
      sortOrder: 'desc',
    });

    return jobs.map((job) => this.convertToSummary(job));
  }

  /**
   * Get jobs by status
   */
  async getJobsByStatus(
    status: string,
    limit = 20
  ): Promise<JobSummary[]> {
    const db = await getGNSSJobDatabase();
    const jobs = await db.queryJobs({
      status,
      limit,
      sortBy: 'uploadedAt',
      sortOrder: 'desc',
    });

    return jobs.map((job) => this.convertToSummary(job));
  }

  /**
   * Search jobs by filename
   */
  async searchJobs(query: string, limit = 20): Promise<JobSummary[]> {
    const db = await getGNSSJobDatabase();
    const allJobs = await db.queryJobs({ limit: 1000 });

    const filtered = allJobs
      .filter(
        (job) =>
          job.filename.toLowerCase().includes(query.toLowerCase()) ||
          job.jobId.toLowerCase().includes(query.toLowerCase())
      )
      .slice(0, limit);

    return filtered.map((job) => this.convertToSummary(job));
  }

  /**
   * Delete a job from history
   */
  async deleteJob(jobId: string): Promise<void> {
    const db = await getGNSSJobDatabase();
    await db.deleteJob(jobId);
  }

  /**
   * Get job statistics
   */
  async getStatistics() {
    const db = await getGNSSJobDatabase();
    return await db.getStatistics();
  }

  /**
   * Export job history to JSON
   */
  async exportHistory(status?: string): Promise<string> {
    const db = await getGNSSJobDatabase();
    return await db.exportJobs(status);
  }

  /**
   * Import job history from JSON
   */
  async importHistory(jsonData: string): Promise<number> {
    const db = await getGNSSJobDatabase();
    return await db.importJobs(jsonData);
  }

  /**
   * Clear old jobs (older than days)
   */
  async clearOldJobs(days: number): Promise<number> {
    const db = await getGNSSJobDatabase();
    const allJobs = await db.queryJobs({ limit: 10000 });

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    let count = 0;
    for (const job of allJobs) {
      const uploadDate = new Date(job.uploadedAt);
      if (uploadDate < cutoffDate) {
        await db.deleteJob(job.jobId);
        count++;
      }
    }

    return count;
  }

  /**
   * Get cached result if available
   */
  async getCachedResult(jobId: string): Promise<GNSSProcessingResult | null> {
    const db = await getGNSSJobDatabase();
    const job = await db.getJob(jobId);

    if (job && job.status === 'completed' && job.result) {
      return job.result;
    }

    return null;
  }

  /**
   * Check if job result is cached
   */
  async isResultCached(jobId: string): Promise<boolean> {
    const result = await this.getCachedResult(jobId);
    return result !== null;
  }

  // Private helper methods

  private convertToHistoryEntry(stored: StoredGNSSJob): JobHistoryEntry {
    return {
      jobId: stored.jobId,
      filename: stored.filename,
      config: stored.config,
      status: stored.status,
      uploadedAt: new Date(stored.uploadedAt),
      startedAt: stored.startedAt ? new Date(stored.startedAt) : undefined,
      completedAt: stored.completedAt
        ? new Date(stored.completedAt)
        : undefined,
      processingDuration: stored.processingDuration,
      result: stored.result,
      error: stored.error,
      fileSize: stored.fileSize,
    };
  }

  private convertToSummary(job: StoredGNSSJob): JobSummary {
    const summary: JobSummary = {
      jobId: job.jobId,
      filename: job.filename,
      status: job.status,
      uploadedAt: new Date(job.uploadedAt).toLocaleString(),
    };

    if (job.completedAt) {
      summary.completedAt = new Date(job.completedAt).toLocaleString();
    }

    if (job.processingDuration) {
      const seconds = Math.round(job.processingDuration);
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      summary.processingTime =
        minutes > 0
          ? `${minutes}m ${secs}s`
          : `${secs}s`;
    }

    if (job.result?.statistics) {
      summary.resultsSummary = {
        pointsProcessed: job.result.statistics.pointsProcessed,
        pdop: job.result.statistics.pdop,
        horizontalRMS: job.result.statistics.horizontalRMS,
        verticalRMS: job.result.statistics.verticalRMS,
      };
    }

    return summary;
  }
}

// Singleton instance
const historyService = new GNSSJobHistoryService();

export default historyService;
